// styles
import styles from "./NaicsCodesSection.module.scss";

import { Icon } from "@trussworks/react-uswds";
import classNames from "classnames";

// store
import { useSearchStore } from "store/searchStore";

// types
import { NaicsProfile } from "types/profile.types";
import { BusinessItem } from "types/results-panel.types";

// components
import DisplayValue from "../DisplayValue";
import { Alert } from "@trussworks/react-uswds";
import { SimpleToopTip } from "components/Tooltip";

// utils
import {
  formatSizeDeterminationDate,
  formatSizeDeterminationText,
  isNaicsExceptionSmall
} from "utils/helper";

type NaicsCodesProps = {
  omitTitle?: boolean;
  naics?: NaicsProfile[];
  businessProfileData?: BusinessItem;
};

export default function NaicsCodesSection({ omitTitle, naics, businessProfileData }: NaicsCodesProps) {
  const size_protest_naics = businessProfileData?.size_protest_naics || [];
  const naics_exception_codes = businessProfileData?.naics_exception_codes || [];

  const tableClasses = classNames(
    "usa-table",
    "usa-table--striped",
    styles["naics-table"]
  );

  const primaryTagClasses = classNames("usa-tag", styles["primary"]);
  const underProtest = businessProfileData?.size_protest;

  const naicsCodes = naics?.length === 1 && naics[0].naics_code === null ? [] : naics;

  return (
    <div className={styles["naics-codes-root"]}>
      {/* allow us to omit title on the edit profile page  */}
      {omitTitle || (
        <h2 id="naics-codes" style={{marginBottom: "16px"}} className={styles['heading']} tabIndex={-1}>
          NAICS codes
        </h2>
      )}

      {underProtest && (
        <Alert
          type="warning"
          headingLevel="h6"
          className={styles["alert-wrapper"]}
        >
          <>
            An SBA formal size determination was made on {formatSizeDeterminationDate(businessProfileData?.size_protest_date)}:{' '}
            <i>
              {formatSizeDeterminationText(businessProfileData?.size_determination_description)}.{' '}
            </i>
            The NAICS codes where this business’s “small” designation have changed are noted below with an exclamation mark icon.
          </>
        </Alert>
      )}

      <DisplayValue
        value={naicsCodes}
        messageElement="div"
        message="This firm does not have any NAICS codes listed"
        renderer={(_value) => (
          <>
            <table className={tableClasses}>
              <thead>
                <tr>
                  <th scope="col">Code</th>
                  <th scope="col">NAICS Code Description</th>
                  <th scope="col">
                    <SimpleToopTip
                      title={`If Yes, the firm's revenues/number of employees do not exceed the NAICS code's small business size standard.`}
                      placement="top"
                    >
                      <div className={styles["col-group"]}>
                        <span>Small</span>
                        <Icon.InfoOutline aria-label="More information about small" />
                      </div>
                    </SimpleToopTip>
                  </th>
                </tr>
              </thead>
              <tbody>
                {naicsCodes &&
                  naicsCodes.map((naics: NaicsProfile, index) => (
                    <tr key={index}>
                      <td scope="row" style={{ verticalAlign: "text-top" }}>
                        {naics?.naics_code ? naics.naics_code : "No NAICS code found"}
                      </td>

                      <td>
                        {naics?.naics_primary && (
                          <span className={primaryTagClasses}>Primary</span>
                        )}
                        {(naics?.naics_exception_indicator && naics?.naics_exception_number !== "1") && ( // 1 is the main naics code, other values are exceptions
                          <div className={styles["exception-container"]}>
                            <SimpleToopTip
                              title={
                                <span>
                                  The size standards can depend on subcategories
                                  within a NAICS code. For more information, see{" "}
                                  <a
                                    href="https://www.sba.gov/document/support-table-size-standards"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    the SBA’s size standards table here.
                                  </a>
                                </span>
                              }
                              placement="top"
                              className={styles["tooltip-wrapper"]}
                            >
                              <div className={styles["size-exception"]}>
                                <span className={"usa-tag"}>
                                  Size standard exception
                                </span>
                                <Icon.InfoOutline aria-label="More information on size standard exceptions" />
                              </div>
                            </SimpleToopTip>
                          </div>
                        )}
                        <p className={styles["heading"]}>{naics?.naics_desc ? naics.naics_desc : "No NAICS description found"}</p>
                      </td>

                      <td>
                        {naics?.naics_exception_indicator && (
                          <div
                            className={styles["check-group"]}
                            style={{
                              color:
                                size_protest_naics.includes(naics?.naics_code) ? "#FFBE2E" :
                                  isNaicsExceptionSmall(naics, naics_exception_codes) ? "#538200" : "#565C65"
                            }}
                          >
                            {
                              size_protest_naics.includes(naics?.naics_code) ? <Icon.Error aria-label="Code has a size protest" /> :
                                isNaicsExceptionSmall(naics, naics_exception_codes) ?
                                  <Icon.Check aria-label="Code is small" /> : <Icon.Cancel aria-label="Code is not small" />
                            }
                            <span>
                              {
                                size_protest_naics.includes(naics?.naics_code) ? "No" :
                                isNaicsExceptionSmall(naics, naics_exception_codes) ? "Yes" : "No"
                              }
                            </span>
                          </div>
                        )}

                        {!naics?.naics_exception_indicator && (
                          <div
                            className={styles["check-group"]}
                            style={{
                              color:
                                size_protest_naics.includes(naics?.naics_code) ? "#FFBE2E" :
                                  naics?.naics_small_business ?
                                    "#538200" : "#565C65"
                            }}
                          >
                            {
                              size_protest_naics.includes(naics?.naics_code) ? <Icon.Error aria-label="Code has a size protest" /> :
                                naics?.naics_small_business ?
                                  <Icon.Check aria-label="Code is small" /> : <Icon.Cancel aria-label="Code is not small" />
                            }
                            <span>
                              {
                                size_protest_naics.includes(naics?.naics_code) ? "No" :
                                naics?.naics_small_business ? "Yes" : "No"
                              }
                            </span>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </>
        )}
      />
    </div>
  );
}
